import React, { useEffect, useMemo, useState } from 'react';
import { Box, FormControlLabel, Paper, Switch, Tooltip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DataGrid, GridColDef, GridFilterModel } from '@mui/x-data-grid';
import { useQuery } from '@tanstack/react-query';
import { collection, documentId, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { OpportunityQueryData } from '../../../loaders/eventsLoaders';
import { extractIdFromSelf } from '../../../hooks/useNavToEvent';
import {
  computeDateCheck,
  deriveEventDate,
  EventDateCheckAck,
  formatEventDateForMessage,
  isDateCheckAcknowledged,
  parseTitleDate
} from '../../../utils/eventDate';
import { DATE_CHECK_ACKS_QUERY_KEY } from '../../../hooks/useAcknowledgeEventDate';
import { useNotifications } from '@toolpad/core';

// Define the row type for proper typing
export interface EventRow extends OpportunityQueryData {
  id: string;
  eventDate: string | null;
  /** True when the app has no stored date for this opportunity yet and the title's date is shown instead. */
  dateFromTitle: boolean;
  dateIssue: boolean;
  dateIssueTooltip: string;
}

const DATE_ISSUE_STATUSES = ['title-mismatch', 'unexpected-forecasted-time'];

interface EventsDataGridProps {
  events: OpportunityQueryData[];
  isLoading: boolean;
  filterModel: GridFilterModel;
  onFilterModelChange: (model: GridFilterModel) => void;
  onRowClick: (params: any) => void;
}

const columns: GridColDef<EventRow>[] = [
  {
    field: 'dateIssue',
    headerName: '',
    width: 48,
    sortable: false,
    filterable: false,
    disableColumnMenu: true,
    renderCell: (params) =>
      params.row.dateIssue ? (
        <Tooltip title={params.row.dateIssueTooltip}>
          <WarningAmberIcon color="warning" fontSize="small" />
        </Tooltip>
      ) : null
  },
  {
    field: 'eventDate',
    headerName: 'Date',
    width: 160,
    filterable: false,
    sortable: true,
    renderCell: (params) =>
      params.row.dateFromTitle && params.row.eventDate ? (
        <Tooltip title="Not opened in the app yet: date taken from the Daylite title">
          <span>{formatEventDateForMessage(params.row.eventDate)} (title)</span>
        </Tooltip>
      ) : (
        formatEventDateForMessage(params.row.eventDate)
      )
  },
  {
    field: 'name',
    headerName: 'Name',
    flex: 1,
    minWidth: 250,
    filterable: true,
    sortable: true
  }
];

const EventsDataGrid: React.FC<EventsDataGridProps> = ({
  events,
  isLoading,
  filterModel,
  onFilterModelChange,
  onRowClick
}) => {
  const [dateIssuesOnly, setDateIssuesOnly] = useState(false);

  // Daylite search returns only `self` and `name` (prod, 2026-11-04), so dates come from the app's own event docs:
  // the canonical dateISO8601, and the check from the stored Daylite payload against the live search title.
  const ids = useMemo(() => Array.from(new Set(events.map((event) => extractIdFromSelf(event.self)))).sort(), [events]);
  const { data: storedEvents } = useQuery({
    queryKey: [...DATE_CHECK_ACKS_QUERY_KEY, ids.join('|')],
    enabled: ids.length > 0,
    queryFn: async () => {
      const map = new Map<string, { dateISO8601: string | null; forecasted: unknown; ack: EventDateCheckAck | null }>();
      for (let start = 0; start < ids.length; start += 30) {
        const snap = await getDocs(query(collection(db, 'events'), where(documentId(), 'in', ids.slice(start, start + 30))));
        snap.forEach((d) => {
          const data = d.data();
          map.set(d.id, {
            dateISO8601: typeof data.dateISO8601 === 'string' && data.dateISO8601 ? data.dateISO8601 : null,
            forecasted: data.dayliteData?.forecasted,
            ack: (data.dateCheckAck as EventDateCheckAck | undefined) ?? null
          });
        });
      }
      return map;
    }
  });

  // Prepare rows for the DataGrid; recalculated only when events or stored event docs change
  const rows = useMemo<EventRow[]>(
    () =>
      events.map((event) => {
        const id = extractIdFromSelf(event.self);
        const stored = storedEvents?.get(id);
        const storedDate = stored?.dateISO8601 ?? (stored ? deriveEventDate(stored.forecasted) : null);
        const check = stored ? computeDateCheck(event.name, stored.forecasted) : null;
        const dateIssue =
          !!check && DATE_ISSUE_STATUSES.includes(check.status) && !isDateCheckAcknowledged(check, stored?.ack ?? null);

        return {
          ...event,
          id,
          eventDate: storedDate ?? parseTitleDate(event.name),
          dateFromTitle: !storedDate,
          dateIssue,
          dateIssueTooltip: dateIssue
            ? `Title says ${formatEventDateForMessage(check?.titleDate)}; Daylite Forecasted says ${formatEventDateForMessage(check?.forecastedDate)}`
            : ''
        };
      }),
    [events, storedEvents]
  );

  const visibleRows = useMemo(() => (dateIssuesOnly ? rows.filter((r) => r.dateIssue) : rows), [rows, dateIssuesOnly]);

  const notifications = useNotifications();
  // Warning notification for large number of events
  useEffect(() => {
    if (rows.length >= 250) {
      notifications.show(
        "Warning: you've reached the max number of events that can be pulled from Daylite at once.  Some results may be missing.  Use the end date filter for accurate results.",
        {
          severity: 'error',
          autoHideDuration: 3000
        }
      );
    }
  }, [rows]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', width: '100%', overflow: 'hidden' }}>
      <FormControlLabel
        control={<Switch checked={dateIssuesOnly} onChange={(e) => setDateIssuesOnly(e.target.checked)} />}
        label="Date issues only"
        sx={{ alignSelf: 'flex-start' }}
      />
      <Paper sx={{ flexGrow: 1, display: 'flex', minHeight: 0 }}>
        <DataGrid
          rows={visibleRows}
          columns={columns}
          disableRowSelectionOnClick
          loading={isLoading}
          onRowClick={onRowClick}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          initialState={{
            sorting: {
              sortModel: [{ field: 'eventDate', sort: 'asc' }]
            }
          }}
        />
      </Paper>
    </Box>
  );
};

export default EventsDataGrid;
